import {
  Checkbox,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import * as React from 'react';
import { ExternalOrderService } from 'services';
import { SnackbarContext } from 'components';

/**
 * Dish tagging for the ordering storefront (2026-09-05).
 *
 * 132 dishes is a long sit-down, so every checkbox saves on the spot rather
 * than collecting into one giant Save at the end: close the tab halfway and
 * nothing is lost. Tags drive the filter pills and the badges on the dish
 * cards at pekingmalta.com/order.
 */
const useStyles = makeStyles((theme) => ({
  intro: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  search: { minWidth: 260 },
  categoryRow: {
    background: theme.palette.grey[100],
  },
  categoryCell: {
    fontWeight: 700,
  },
  dishCell: {
    minWidth: 260,
  },
  desc: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
  },
  tagCell: {
    textAlign: 'center',
    padding: 0,
  },
  counts: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
  },
}));

interface TagDef {
  key: string;
  label: string;
}

interface Dish {
  id: number;
  title: string;
  description: string | null;
  tags: string[];
}

interface Group {
  categoryId: number;
  category: string;
  section: string;
  items: Dish[];
}

export const DishTagsTab: React.FC = () => {
  const classes = useStyles();
  const snackbar = React.useContext(SnackbarContext);
  const [tags, setTags] = React.useState<TagDef[]>([]);
  const [groups, setGroups] = React.useState<Group[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [saving, setSaving] = React.useState<Record<number, boolean>>({});

  React.useEffect(() => {
    setLoading(true);
    ExternalOrderService.websiteGetDishTags()
      .then((r: any) => {
        setTags(r?.tags || []);
        setGroups(r?.groups || []);
      })
      .catch((e: any) => snackbar.error(e))
      .finally(() => setLoading(false));
  }, [snackbar]);

  const toggle = (dish: Dish, tag: string) => {
    const next = dish.tags.includes(tag)
      ? dish.tags.filter((t) => t !== tag)
      : [...dish.tags, tag];

    // Optimistic: the checkbox moves now, and rolls back if the save fails.
    const apply = (value: string[]) =>
      setGroups((prev) =>
        prev.map((g) => ({
          ...g,
          items: g.items.map((i) =>
            i.id === dish.id ? { ...i, tags: value } : i,
          ),
        })),
      );

    const before = dish.tags;
    apply(next);
    setSaving((s) => ({ ...s, [dish.id]: true }));
    ExternalOrderService.websiteSaveDishTags({
      menuItemId: dish.id,
      tags: next,
    })
      .catch((e: any) => {
        apply(before);
        snackbar.error(e);
      })
      .finally(() => setSaving((s) => ({ ...s, [dish.id]: false })));
  };

  const needle = search.trim().toLowerCase();
  const visible = groups
    .map((g) => ({
      ...g,
      items: needle
        ? g.items.filter(
            (i) =>
              i.title.toLowerCase().includes(needle) ||
              (i.description || '').toLowerCase().includes(needle),
          )
        : g.items,
    }))
    .filter((g) => g.items.length > 0);

  const totalDishes = groups.reduce((n, g) => n + g.items.length, 0);
  const taggedDishes = groups.reduce(
    (n, g) => n + g.items.filter((i) => i.tags.length > 0).length,
    0,
  );

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <Paper className={classes.intro} elevation={0} variant="outlined">
        <Typography variant="body2">
          Tick what each dish is. These show as filter buttons and badges on
          the ordering page at pekingmalta.com/order. Every tick saves by
          itself, so you can stop and come back.
        </Typography>
        <Typography variant="body2" style={{ marginTop: 8, color: '#b71c1c' }}>
          Gluten-free is a promise to someone who can get ill. Only tick it
          where the kitchen is sure of the sauce and the fryer.
        </Typography>
      </Paper>

      <div className={classes.toolbar}>
        <TextField
          className={classes.search}
          size="small"
          variant="outlined"
          placeholder="Search dishes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <span className={classes.counts}>
          {taggedDishes} of {totalDishes} dishes tagged
        </span>
        {tags.map((t) => (
          <Chip
            key={t.key}
            size="small"
            label={`${t.label}: ${groups.reduce(
              (n, g) => n + g.items.filter((i) => i.tags.includes(t.key)).length,
              0,
            )}`}
          />
        ))}
      </div>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.dishCell}>Dish</TableCell>
              {tags.map((t) => (
                <TableCell key={t.key} align="center">
                  {t.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((g) => (
              <React.Fragment key={g.categoryId}>
                <TableRow className={classes.categoryRow}>
                  <TableCell
                    className={classes.categoryCell}
                    colSpan={tags.length + 1}
                  >
                    {g.section} · {g.category}
                  </TableCell>
                </TableRow>
                {g.items.map((dish) => (
                  <TableRow key={dish.id} hover>
                    <TableCell className={classes.dishCell}>
                      <div>{dish.title}</div>
                      {dish.description ? (
                        <div className={classes.desc}>{dish.description}</div>
                      ) : null}
                    </TableCell>
                    {tags.map((t) => (
                      <TableCell key={t.key} className={classes.tagCell}>
                        <Checkbox
                          size="small"
                          checked={dish.tags.includes(t.key)}
                          disabled={!!saving[dish.id]}
                          onChange={() => toggle(dish, t.key)}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
